import { RoutesList } from '@/routes/routesList';
import { useUserInfo } from '@/services/user/hooks';
import rem from '@/utils/rem';
import { Box, Button, Flex, Heading, Spinner, Text, useToast } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const withFeatureFlagAuthorization = (Component: React.ComponentType) => {
  return function WithFeatureFlagAuth(props: any) {
    const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
    const { data: userInfo, isLoading, error } = useUserInfo();
    const navigate = useNavigate();
    const toast = useToast();

    useEffect(() => {
      if (isLoading || !userInfo) return;

      const hasAccess = userInfo.me.isAllowedFeatureFlag;
      setIsAuthorized(hasAccess);
    }, [userInfo, isLoading, toast]);

    if (isLoading || isAuthorized === null) {
      return (
        <Flex alignItems="center" justifyContent="center" flexDirection="column" gap={4} height="100vh">
          <Spinner color="blue.500" size="xl" />
          <Text>Checking permissions...</Text>
        </Flex>
      );
    }

    if (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return (
        <Box maxWidth="800px" margin="0 auto" padding={rem(32)} textAlign="center">
          <Heading as="h1" marginBottom={rem(16)} size="xl">
            Error Checking Permissions
          </Heading>
          <Text marginBottom={rem(24)} fontSize="lg">
            There was an error while checking your permissions: {errorMessage}
          </Text>
          <Button colorScheme="blue" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </Box>
      );
    }

    if (!isAuthorized) {
      return (
        <Box maxWidth="800px" margin="0 auto" padding={rem(32)} textAlign="center">
          <Heading as="h1" marginBottom={rem(16)} size="xl">
            Access Denied
          </Heading>
          <Text marginBottom={rem(24)} fontSize="lg">
            You don't have permission to access the feature flags administration. Please contact your administrator to
            request access.
          </Text>
          <Button colorScheme="blue" onClick={() => navigate(RoutesList.HomePage)}>
            Return to Home
          </Button>
        </Box>
      );
    }

    return <Component {...props} />;
  };
};

export default withFeatureFlagAuthorization;
